import { logStartGreyError, logStartUsage } from 'owa-analytics-start';
import { getEnabledFlightsWithPrefix } from 'owa-feature-flags';
import { getBootFlights, isBootFeatureEnabled } from 'owa-metatags';
import { tryGetGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import {
    getBootCustomData,
    getBootTimings,
    getBottlenecks,
    getBottleneckTimings,
    getLayers,
    markEnd,
    markStart,
} from 'owa-performance';
import { getHostLocation } from 'owa-url/lib/hostLocation';
import { PLACES_APP_START_TIME, PLACES_TELEMETRY_TRACKING_ID } from './constants';
import getEntryPoint from './utils/getEntryPoint';
import type { MailboxInfo } from 'owa-client-types';

import type { CustomDataMap, TraceErrorObject } from 'owa-analytics-types';

const STEP_LIST = [
    /**
     * Phase 1a: App start
     */
    'AppStart',

    /**
     * Phase 1b: MetaOS initialization
     */
    // Do not log this step, as it is captured in the "owa-start" package
    // packages/framework/owa-start/src/getRunBeforeStartForHosted.ts

    /**
     * Phase 2: Session & JS bundle
     */
    'PlacesSessionData',
    'PlacesBootBundle',
    'PlacesMainBundle',

    /**
     * Phase 3: Store & Preload
     */
    'PlacesSettings',

    /**
     * Phase 4: Render
     */
    'AppBootEnd',
] as const;

export type PlacesAppBootStep = typeof STEP_LIST[number];

/**
 * Why not use PerformanceDatapoint Waterfall?
 * We want to send the logs out ASAP
 * to avoid losing them when the WINDOW CLOSES SUDDENLY.
 */
export function logAppBootStep(
    stepName: PlacesAppBootStep,
    customData?: Readonly<CustomDataMap>,
    error?: unknown
): void {
    let log = collectAppBootStepInfo(stepName, error);

    if (!log) {
        return;
    }

    log = {
        ...log,
        ...(stepName === 'AppBootEnd' ? collectPerformanceInfo() : {}), // Only append to the last step
        ...(stepName === 'AppBootEnd' ? collectRuntimeInfo() : {}), // Only append to the last step
        ...customData, // Allow to override by the caller
    };

    const mailboxInfo = tryGetMailbox();

    if (stepName === 'AppStart') {
        logStartUsage('PlacesAppStart', mailboxInfo, log);
    } else if (stepName === 'AppBootEnd') {
        logStartUsage('PlacesAppBootEnd', mailboxInfo, log);
    } else if (isEnabled()) {
        switch (stepName) {
            case 'PlacesSessionData':
                logStartUsage('PlacesSessionData', mailboxInfo, log);
                break;
            case 'PlacesBootBundle':
                logStartUsage('PlacesBootBundle', mailboxInfo, log);
                break;
            case 'PlacesMainBundle':
                logStartUsage('PlacesMainBundle', mailboxInfo, log);
                break;
            case 'PlacesSettings':
                logStartUsage('PlacesSettings', mailboxInfo, log);
                break;
        }
    }
}

export function collectAppBootStepInfo(
    stepName: PlacesAppBootStep,
    error?: unknown
): CustomDataMap | undefined {
    try {
        const now = performance.now();
        const elapsedTime = now - PLACES_APP_START_TIME;

        let reason: string | undefined;
        let diagnostics: string | undefined;

        try {
            if (error instanceof Error) {
                reason = error.message;
                diagnostics = JSON.stringify({
                    ...(error as TraceErrorObject).additionalInfo,
                    diagnosticInfo: (error as TraceErrorObject).diagnosticInfo,
                });
            } else if (typeof error === 'string') {
                reason = error;
            } else if (error != null) {
                reason = (error as Error | undefined)?.message ?? String(error);
            }
        } catch {}

        return {
            version: 2, // Upgrade the version when we change the format or the step list
            step_track_id: PLACES_TELEMETRY_TRACKING_ID,
            step_time: now + performance.timeOrigin,
            step_elapsed_time: elapsedTime,
            step_user_click_time: getBootCustomData()?.HubLoadData?.userClickTime, // Time of user clicking on the Host hub
            step_app_start_time: PLACES_APP_START_TIME + performance.timeOrigin, // Help calculating elapsed time from the click time
            step_name: stepName,
            step_order: STEP_LIST.indexOf(stepName) + 1,
            step_path: getHostLocation().pathname,
            step_entry_point: getEntryPoint(),
            hostSessionId: getBootCustomData().HostSessionId,
            hasError: error != null,
            error: reason,
            diagnostics,
        };
    } catch (err) {
        logStartGreyError(
            'PlacesAppBootE2EError',
            tryGetMailbox(),
            err instanceof Error ? err : undefined,
            {
                step_track_id: PLACES_TELEMETRY_TRACKING_ID,
                step_name: stepName,
                step_order: STEP_LIST.indexOf(stepName) + 1,
            }
        );

        return undefined;
    }
}

export function collectPerformanceInfo(): CustomDataMap | undefined {
    try {
        let BottleneckTimings = getBottleneckTimings();
        if (typeof BottleneckTimings !== 'string') {
            BottleneckTimings = JSON.stringify(BottleneckTimings);
        }

        let Bottlenecks = getBottlenecks();
        if (typeof Bottlenecks !== 'string') {
            Bottlenecks = JSON.stringify(Bottlenecks);
        }

        return {
            BottleneckTimings,
            Bottlenecks,
            Layers: getLayers(),
            Timings: getBootTimings(),
        };
    } catch {
        return undefined;
    }
}

export function collectRuntimeInfo(): CustomDataMap | undefined {
    try {
        return {
            BootFlights: getBootFlights()
                .filter(flight => flight.includes('places'))
                .join(','),
            FeatureFlags: getEnabledFlightsWithPrefix('msplaces')
                .concat(getEnabledFlightsWithPrefix('places'))
                .join(','),
        };
    } catch {
        return undefined;
    }
}

export function startAppBootStep(
    stepName: PlacesAppBootStep,
    customData?: Readonly<CustomDataMap>
) {
    const startTime = performance.now();
    markStart(stepName);

    return {
        hasError: false,
        caughtError: undefined as unknown | undefined,
        end(customData2?: Readonly<CustomDataMap>): void {
            markEnd(stepName);
            logAppBootStep(
                stepName,
                {
                    ...customData,
                    ...customData2,
                    hasError: this.hasError,
                    duration: performance.now() - startTime,
                },
                this.caughtError
            );
        },
    };
}

export async function markAppBootStep<T>(
    promise: Promise<T>,
    stepName: PlacesAppBootStep,
    customData?: Readonly<CustomDataMap>
): Promise<T> {
    const bootStep = startAppBootStep(stepName, customData);

    try {
        return await promise;
    } catch (err) {
        bootStep.hasError = true;
        bootStep.caughtError = err;
        throw err;
    } finally {
        bootStep.end();
    }
}

function isEnabled() {
    return isBootFeatureEnabled('msplaces-app-boot-sequence');
}

function tryGetMailbox(): MailboxInfo | undefined {
    try {
        return tryGetGlobalSettingsAccountMailboxInfo();
    } catch {
        return undefined;
    }
}
